import { useEffect, useState } from "react";
import Flex from "../Flex/Flex";
import Button from "../forms/Button/Button";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import Plan from "@/app/auth/(signup)/Plan";
import { changePlan, changePlanPreview, getPlan } from "@/api/billing";
import { RecurlyPlan } from "@/lib/recurly";
import styles from "./ChangeSubscriptionModal.module.css";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";

export enum ChangeSubscriptionModalMode {
  closed,
  update,
};

export interface ChangeSubscriptionModalProps {
  targetPlanCode: string;
  onClose?: () => void;
  onSuccess?: () => void;
  mode?: ChangeSubscriptionModalMode;
  setMode?: (mode: ChangeSubscriptionModalMode) => void;
};

export default function ChangeSubscriptionModal(props: ChangeSubscriptionModalProps) {
  const { subscription, setSubscription } = useSubscriptionContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [prefetchingPlan, setPrefetchingPlan] = useState<boolean>(true);
  const [prefetchingPreview, setPrefetchingPreview] = useState<boolean>(true);
  const { mode, targetPlanCode, setMode, onClose, onSuccess } = props;
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { showNotification } = useNotificationContext();

  const [plan, setPlan] = useState<RecurlyPlan | null>(null);
  const [prorate, setProrate] = useState<number>(0.0);
  const [error, setError] = useState<string>(null);

  const titleText = "Upgrade Subscription";
  const okButtonText = "Complete";

  useEffect(() => {
    if (mode === ChangeSubscriptionModalMode.update && plan === null) {
      setPrefetchingPlan(true);
      getPlan(targetPlanCode)
        .then(planRes => setPlan(planRes))
        .catch(err => showNotification("Failed to load target plan. Please try again later.", NotificationType.ERROR))
        .finally(() => {
          setPrefetchingPlan(false);
        });
    }
  }, [mode, targetPlanCode]);

  useEffect(() => {
    //console.log('change subscription loading plan: ', targetPlanCode);
    if (mode === ChangeSubscriptionModalMode.update && !!targetPlanCode && subscription && !!subscription.planCode) {
      if (targetPlanCode !== subscription.planCode) {
        setPrefetchingPreview(true);

        // load preview for prorate charges/credits
        changePlanPreview(subscription?.subscriptionId, targetPlanCode).then((res) => {
          if (res.charge?.total != null) {
            setProrate(res.charge.total);
          }
          else if (res.credit) {
            setProrate(-res.credit.total);
          }
        })
          .catch(err => {
            showNotification("Failed to load plan prorate data. Please try again later", NotificationType.ERROR);
          })
          .finally(() => {
            setPrefetchingPreview(false);
          });
      }
      else {
        showNotification("Target plan is the same as current plan. No need to change.", NotificationType.ERROR);
      }
    }
  }, [mode, subscription, targetPlanCode])

  return (
    <Modal
      open={mode !== ChangeSubscriptionModalMode.closed}
      onOpenChange={() => {
        setMode?.(ChangeSubscriptionModalMode.closed);
        setPlan(null);
      }}
    >
      <ModalHeader><h3>{titleText}</h3></ModalHeader>

      <ModalBody>
        {(prefetchingPlan || prefetchingPreview) ? (
          <div className={styles.loadingSpinnerContainer}>
            <LoadingSpinner />
          </div>
        ) : plan ? (
          <Plan plan={plan} prorate={prorate} />
        ) : (
          <div className={styles.error}>Failed to load subscription plan. Please try again.</div>
        )}
      </ModalBody>

      <ModalFooter>
        <Flex
          justify="end"
          gap={2}
        >
          {loading ? (<LoadingSpinner />) : (
            <>
              <Button
                type="secondary"
                onClick={() => {
                  setMode(ChangeSubscriptionModalMode.closed);
                  setPlan(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={!!error}
                onClick={() => {
                  setLoading(true);
                  changePlan(subscription.subscriptionId, plan.code, plan.name).then(newSubscription => {
                    setSubscription(newSubscription.subscription);
                    setLoading(false);
                    setMode(ChangeSubscriptionModalMode.closed);
                    setPlan(null);
                    onSuccess?.(); // Call the success callback if provided
                  })
                    .catch(errors => {
                      handleErrorResponseGenerically(errors);
                      setLoading(false);
                    });
                }}
              >
                {okButtonText}
              </Button>
            </>
          )}
        </Flex>
      </ModalFooter>
    </Modal>
  );
}
